import React from 'react';
import { useParams } from 'react-router-dom';
import Seo from '@/components/Seo';
import { Button, Container, Icon } from '@/components/ui';
import { blogContent } from '@/content/blog';
import NotFound from '@/pages/NotFound';

function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
}

/**
 * Blog post detail page template (§11 of
 * `docs/TEMPLATE_IMPROVEMENT_RECOMMENDATIONS.md`). Looks up the post by the
 * `:slug` route param in `src/content/blog.ts`; renders the 404 page for an
 * unknown slug. See `docs/CMS_ADAPTER.md` for backing this with a real CMS.
 */
const BlogPost: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const post = blogContent.posts.find((p) => p.slug === slug);

    if (!post) return <NotFound />;

    return (
        <>
            <Seo
                title={post.title}
                description={post.excerpt}
                path={`/blog/${post.slug}`}
                breadcrumbs={[{ name: blogContent.title, path: '/blog' }]}
                article={{ publishedTime: post.date, author: post.author }}
            />
            <article className="blog-post">
                <Container className="blog-post-content">
                    <a className="blog-post-back" href="/blog">
                        <Icon name="arrow-right" style={{ transform: 'rotate(180deg)' }} /> Back to
                        news
                    </a>
                    <h1 className="blog-post-title">{post.title}</h1>
                    <p className="blog-card-meta">
                        <span>
                            <Icon name="calendar" />{' '}
                            <time dateTime={post.date}>{formatDate(post.date)}</time>
                        </span>
                        <span>
                            <Icon name="user" /> {post.author}
                        </span>
                    </p>
                    {post.content.map((paragraph, index) => (
                        <p className="blog-post-paragraph" key={index}>
                            {paragraph}
                        </p>
                    ))}
                    <Button as="a" href="/contact" variant="primary">
                        Get in touch
                    </Button>
                </Container>
            </article>
        </>
    );
};

export default BlogPost;
